import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowTopRightOnSquareIcon,
  CpuChipIcon,
  ChartBarIcon,
  CogIcon,
  RocketLaunchIcon,
  ShieldCheckIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { products } from '../data/products';

const Home: React.FC = () => {
  const features = [ 
    {
      icon: ChartBarIcon,
      title: 'Data & Analytics',
      description: 'Unlock the power of your data to extract insights, drive smarter decisions, optimize operations, and uncover revenue opportunities',
    },
    {
      icon: CogIcon,
      title: 'AI Products & Services',
      description: 'Build end-to-end AI solutions or integrate existing products with AI systems alongside ongoing support and optimization.',
    },
    {
      icon: CpuChipIcon,
      title: 'AI Strategy Consulting',
      description: 'Craft a winning AI strategy that aligns innovation with business objectives, reduces risk, and delivers measurable ROI',
    },
    {
      icon: RocketLaunchIcon,
      title: 'Generative AI',
      description: 'Create personalized experiences, increased productivity, enhanced creativity and new possibilities with LLMs and other AI models. ',
    },
    {
      icon: ShieldCheckIcon,
      title: 'AI Agents',
      description: 'Automate to create personalized user experience with  AI agents that perform complex tasks, streamline workflows, and reduce costs.',
    },
    {
      icon: UserGroupIcon,
      title: 'AI Chatbots',
      description: 'Deliver smarter, faster, and more personalized customer interactions with AI chatbots that boost engagement and reduce support costs.',
    },
  ];

  const stats = [
    { number: 'End-to-End AI', label: 'Complete Solutions' },
    { number: 'Legacy System Integration', label: 'Plug-In AI' },
    { number: 'Quick Turnaround', label: 'Time-to-Market' },
    { number: 'Research Triangle Park (RTP)', label: 'Your Local AI Partner' },

  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-50 to-secondary-50 section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-secondary-900 mb-6">
                Transform Your Business with{' '}
                <span className="text-primary-500">AI Solutions</span>
              </h1>
              <p className="text-md text-secondary-600 mb-8">
                We offer AI products and services with cutting-edge
                technologies and innovative solutions, helping businesses leverage the power of AI
                for higher growth and efficiency.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact" className="btn-primary text-center">
                  Get Started Today
                </Link>
                <a href="#products" className="btn-secondary text-center">
                  Products
                </a>
                <Link to="/services" className="btn-secondary text-center">
                  Services
                </Link>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="bg-white">
                <img src="/services1.png" alt="AI Solutions" className="w-full h-full" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-white pt-4 pb-4">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-1">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-l md:text-l whitespace-nowrap font-bold text-primary-600 border-2 border-gray-200 p-2">
                  {stat.number}
                </div>
                <div className="text-secondary-600">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="bg-secondary-50 section-padding scroll-mt-24">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-secondary-900 mb-4">
              Our AI Products
            </h2>           
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {products.map((product, index) => (
              <motion.div
                key={product.name}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="flex flex-col bg-white border border-secondary-200 rounded-xl p-8 hover:shadow-xl transition-shadow duration-300"
              >
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
                  <product.icon className="w-6 h-6 text-primary-600" />
                </div>
                <h3 className="text-xl font-semibold text-secondary-900 mb-1">
                  {product.name}
                </h3>
                <p className="text-primary-600 font-medium mb-3">
                  {product.tagline}
                </p>
                <p className="text-secondary-600 text-md mb-4">
                  {product.description}
                </p>
                <ul className="space-y-2 mb-6">
                  {product.highlights.map((highlight) => (
                    <li
                      key={highlight}
                      className="flex items-start text-sm text-secondary-600"
                    >
                      <span className="w-1.5 h-1.5 bg-primary-500 rounded-full mt-1.5 mr-2 flex-shrink-0" />
                      {highlight}
                    </li>
                  ))}
                </ul>
                <a
                  href={product.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-auto inline-flex items-center gap-1 text-primary-600 font-medium hover:text-primary-700 transition-colors duration-200"
                >
                  Visit {product.domain}
                  <ArrowTopRightOnSquareIcon className="w-4 h-4" aria-hidden="true" />
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="bg-white section-padding">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-secondary-900 mb-4">
              Our AI Services
            </h2>      
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-primary-600" />
                </div>
                <h3 className="text-xl font-semibold text-secondary-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-secondary-600 text-md">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-primary-600 section-padding">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-xl md:text-2xl font-bold text-white mb-4">
              Ready to transform your business?
            </h2>
            <p className="text-lg text-primary-100 mb-8 max-w-2xl mx-auto">
              Let's discuss how our AI products and services can drive innovation and growth.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="btn-primary text-center bg-white text-primary-600 hover:bg-secondary-100 transition-colors duration-200">
                Schedule a Consultation
                </Link>
              <Link
                to="/services"
                className="btn-primary text-center bg-white text-primary-600 hover:bg-secondary-100 transition-colors duration-200">
                Learn More
                </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;

